import React, { createContext, useContext, useState, useEffect } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { apiRequest } from './authConfig';

interface AuthContextType {
    isAuthenticated: boolean;
    isLoading: boolean;
    user: any;
    getAccessToken: () => Promise<string>;
    login: () => Promise<void>;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { instance, accounts, inProgress } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        // Handle redirect promise after login
        instance.handleRedirectPromise().then((response) => {
            if (response) {
                // Successfully completed authentication
                console.log('Authentication successful');
            }
            setIsLoading(false);
        }).catch((error) => {
            // Handle the specific error when cache is cleared
            if (error?.errorCode === 'no_token_request_cache_error') {
                console.warn('Authentication state was lost. Clearing auth state and reloading...');
                // Clear any partial auth state and reload the page
                instance.clearCache();
                window.location.reload();
            } else {
                console.error('Redirect error:', error);
                setIsLoading(false);
            }
        });
    }, [instance]);

    useEffect(() => {
        setIsLoading(inProgress !== InteractionStatus.None);
    }, [inProgress]);

    const getAccessToken = async (): Promise<string> => {
        const account = accounts[0];
        
        if (!account) {
            throw new Error('No account found. Please sign in.');
        }

        const request = {
            ...apiRequest,
            account: account,
            forceRefresh: false // Allow using cached token if valid
        };

        try {
            // Try to acquire token silently
            const response = await instance.acquireTokenSilent(request);
            return response.accessToken;
        } catch (error) {
            if (error instanceof InteractionRequiredAuthError) {
                // If silent acquisition fails, try redirect instead of popup for better UX
                try {
                    await instance.acquireTokenRedirect(request);
                    // This will redirect the user, so we won't reach here
                    throw new Error('Redirecting for authentication...');
                } catch (redirectError) {
                    console.error('Token acquisition failed:', redirectError);
                    throw redirectError;
                }
            }
            throw error;
        }
    };

    const login = async () => {
        try {
            await instance.loginRedirect(apiRequest);
        } catch (error) {
            console.error('Login error:', error);
        }
    };

    const logout = () => {
        instance.logoutRedirect({
            postLogoutRedirectUri: window.location.origin,
        });
    };

    const value: AuthContextType = {
        isAuthenticated,
        isLoading,
        user: accounts[0],
        getAccessToken,
        login,
        logout,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
